import { create } from 'zustand';
import { useGetActiveBatchesWithActiveNode } from '@/components/Recipe/RunFlow/batches.store';

/** Marks an Enhance run whose request has not returned a batch id yet. */
const STARTING = 'starting';

interface Seedance25EnhanceRun {
  batchId: string;
  /** The draft result being enhanced. */
  assetId: string;
}

interface Seedance25EnhanceState {
  /** Each node's latest Enhance run, keyed by node id. */
  runByNodeId: Record<string, Seedance25EnhanceRun>;
  setRun: (nodeId: string, run: Seedance25EnhanceRun | null) => void;
}

/**
 * Which batch is enhancing a node, and which draft it renders. The run's params are one-off, so
 * nothing on the node says it is enhancing; Generate high res reads this to show Generating….
 */
export const useSeedance25EnhanceStore = create<Seedance25EnhanceState>()((set) => ({
  runByNodeId: {},
  setRun: (nodeId, run) =>
    set((state) => {
      const { [nodeId]: _, ...rest } = state.runByNodeId;
      return { runByNodeId: run ? { ...rest, [nodeId]: run } : rest };
    }),
}));

/** Records an Enhance of `assetId` on the node while `run` starts it; `run` resolves to the batch id. */
export async function trackSeedance25Enhance(nodeId: string, assetId: string, run: () => Promise<string | null>) {
  const { setRun } = useSeedance25EnhanceStore.getState();
  setRun(nodeId, { batchId: STARTING, assetId });
  try {
    const batchId = await run();
    setRun(nodeId, batchId ? { batchId, assetId } : null);
    return batchId;
  } catch (error) {
    setRun(nodeId, null);
    throw error;
  }
}

/**
 * The draft asset id the node is enhancing right now (starting, or its batch still active), or
 * undefined when it is not running an Enhance.
 */
export function useSeedance25EnhancingAssetId(nodeId: string): string | undefined {
  const run = useSeedance25EnhanceStore((state) => state.runByNodeId[nodeId]);
  const activeBatches = useGetActiveBatchesWithActiveNode(nodeId);
  if (!run) {
    return undefined;
  }
  const isActive = run.batchId === STARTING || activeBatches.some((batch) => batch.batchId === run.batchId);
  return isActive ? run.assetId : undefined;
}
